import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react';
import api from '../api/client';
import { getEcho } from '../lib/echo';
import { showLocalNotification, subscribeToPushNotifications } from '../lib/pushNotifications';
import { useAuth } from '../hooks/useAuth';
import { useCallStore } from '../stores/useCallStore';
import { CallContext } from './CallContext';
import type { LocalCallStatus } from '../stores/useCallStore';

import type {
  ConversationCall,
  CallSignal,
  CallInitiatedEvent,
  CallAcceptedEvent,
  CallRejectedEvent,
  CallEndedEvent,
  CallSignalEvent,
  User,
} from '../types';

type EchoInstance = Exclude<ReturnType<typeof getEcho>, null>;
type EchoChannel = ReturnType<EchoInstance['private']>;

interface ConversationSubscription {
  channel: EchoChannel;
  cleanup: () => void;
}

interface QueuedSignal {
  type: 'offer' | 'answer';
  payload: RTCSessionDescriptionInit;
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const {
    setActiveCall: setActiveCallStore,
    setCallStatus: setCallStatusStore,
    setConnectionState: setConnectionStateStore,
    setCallDuration: setCallDurationStore,
    setCallError: setCallErrorStore,
    setCallFlags: setCallFlagsStore,
    resetCallState: resetCallStateStore,
  } = useCallStore();

  const [activeCall, setActiveCallReact] = useState<ConversationCall | null>(null);
  // callStatus/callDuration/callError artık sadece store'da tutulur (context value
  // memo'landığı için provider'ın bu değerleri local state'te okumasına gerek yok);
  // connectionState provider içinde okunmaya devam ettiği için local state kalır.
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [isLocalVideoLarge, setIsLocalVideoLarge] = useState(false);
  const [isBackCamera, setIsBackCamera] = useState(false);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const isSpeakerOnRef = useRef(false);

  const setActiveCall = useCallback(
    (
      call:
        | ConversationCall
        | null
        | ((prev: ConversationCall | null) => ConversationCall | null)
    ) => {
      const next =
        typeof call === 'function' ? call(activeCallRef.current) : call;
      setActiveCallReact(next);
      setActiveCallStore(next);
    },
    [setActiveCallStore]
  );

  const setCallStatus = useCallback((status: LocalCallStatus) => {
    setCallStatusStore(status);
  }, [setCallStatusStore]);

  const setCallDuration = useCallback((duration: number) => {
    setCallDurationStore(duration);
  }, [setCallDurationStore]);

  const setCallError = useCallback((error: string | null) => {
    setCallErrorStore(error);
  }, [setCallErrorStore]);

  // Çift yazım tamamlaması: connectionState ve UI flag'leri de store'a yansıtılır.
  // Böylece tüketiciler yüksek frekanslı state'i context yerine store selector'larından
  // okuyabilir ve context value memo'lanabilir (1 Hz callDuration re-render'ı önlenir).
  useEffect(() => {
    setConnectionStateStore(connectionState);
  }, [connectionState, setConnectionStateStore]);

  useEffect(() => {
    setCallFlagsStore({ isMuted, isVideoEnabled, isSpeakerOn, isLocalVideoLarge, isBackCamera });
  }, [isMuted, isVideoEnabled, isSpeakerOn, isLocalVideoLarge, isBackCamera, setCallFlagsStore]);

  const activeCallRef = useRef<ConversationCall | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const targetUserIdRef = useRef<number | null>(null);
  const iceRestartAttemptsRef = useRef(0);
  const subscriptionsRef = useRef<Map<number, ConversationSubscription>>(new Map());
  // subscribeAll ile abone olunan kalıcı sohbetler; aktif çağrı effect'inin
  // cleanup'ı bu kanalların aboneliğini kaldırmamalı, yoksa çağrı bittikten
  // sonra gelen arama bildirimleri o sohbet kanalından alınamaz.
  const permanentConversationsRef = useRef<Set<number>>(new Set());
  const userChannelRef = useRef<{ channel: EchoChannel; cleanup: () => void } | null>(null);
  const callDurationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const signalQueueRef = useRef<QueuedSignal[]>([]);
  const iceCandidateQueueRef = useRef<RTCIceCandidateInit[]>([]);
  const iceCandidateBatchRef = useRef<RTCIceCandidateInit[]>([]);
  const iceCandidateBatchTimerRef = useRef<number | null>(null);
  const warmupPeerRef = useRef<RTCPeerConnection | null>(null);
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);
  const ringbackRef = useRef<HTMLAudioElement | null>(null);
  const visibilityRingtoneIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const remoteAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const makingOfferRef = useRef(false);
  const pendingReadyForOfferRef = useRef<number | null>(null);
  const readyForOfferProcessedRef = useRef(false);
  const prewarmPromiseRef = useRef<Promise<void> | null>(null);
  const endCallRef = useRef<(callId: number) => Promise<void>>(async () => {});

  // TURN credentials cache: her çağrı öncesi API çağrısını önlemek için bellek
  // içinde TTL ile saklarız. TTL, TURN credential'ların genelde 24 saat
  // geçerli olduğu varsayımına göre 23 saat olarak seçildi.
  const iceServersCacheRef = useRef<{ servers: RTCIceServer[]; expiresAt: number } | null>(null);

  // Çağrı kurulum metrikleri: test ve izleme için window objesine yazılır.
  // Sadece mevcut çağrıya ait zaman damgalarını tutar; yeni çağrı başladığında
  // sıfırlanır.
  const callMetricsRef = useRef<Record<string, number>>({});

  const recordMetric = useCallback((key: string, timestamp?: number) => {
    const ts = timestamp ?? performance.now();
    callMetricsRef.current[key] = ts;
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__callMetrics = callMetricsRef.current;
    }
  }, []);

  const resetMetrics = useCallback(() => {
    callMetricsRef.current = {};
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__callMetrics = callMetricsRef.current;
    }
  }, []);

  // Abonelik handler'larının her zaman güncel callback'leri çalıştırmasını sağla.
  // useEffect [user?.id] ile abonelikler bir kere kurulduğu için callback ref'leri kullanılır.
  const handleCallInitiatedRef = useRef<(e: CallInitiatedEvent) => void>(() => {});
  const handleCallAcceptedRef = useRef<(e: CallAcceptedEvent) => Promise<void>>(async () => {});
  const handleCallRejectedRef = useRef<(e: CallRejectedEvent) => void>(() => {});
  const handleCallEndedRef = useRef<(e: CallEndedEvent) => void>(() => {});
  const handleSignalRef = useRef<(e: CallSignalEvent) => Promise<void>>(async () => {});

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    isSpeakerOnRef.current = isSpeakerOn;
  }, [isSpeakerOn]);

  const initAudioContext = useCallback(async () => {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioCtx();
    }
    if (audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
      } catch {
        // ignore
      }
    }
  }, []);

  const getIceServers = useCallback(async (): Promise<RTCIceServer[]> => {
    const now = Date.now();
    const cached = iceServersCacheRef.current;
    if (cached && cached.expiresAt > now) {
      return cached.servers;
    }

    // Temel fallback: Google STUN sunucuları.
    const servers: RTCIceServer[] = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];

    try {
      const res = await api.get<{ data: { urls: string[]; username: string; credential: string } }>(
        '/api/v1/turn-credentials'
      );
      const turn = res.data.data;
      if (turn?.urls?.length) {
        servers.push({
          urls: turn.urls,
          username: turn.username,
          credential: turn.credential,
        });
        // Canlı TURN credential TTL'i 1 saat (TURN_USERNAME_TTL=3600);
        // bayat credential relay 401 verir, bu yüzden 55 dk cache'le.
        iceServersCacheRef.current = {
          servers: [...servers],
          expiresAt: now + 55 * 60 * 1000,
        };
      }
    } catch (err) {
      // TURN endpoint'i yapılandırılmamış veya erişilemezse STUN ile devam et.
      console.warn('[WebRTC] TURN credentials alınamadı, STUN fallback kullanılıyor:', err);
    }

    return servers;
  }, []);

  const warmupIceServers = useCallback(async () => {
    // TURN credential'larını al ve TURN sunucusuyla allocation/connection
    // warm-up yap. Böylece ilk çağrıdaki TURN el sıkışma gecikmesi kısalır.
    if (warmupPeerRef.current) return;

    try {
      const iceServers = await getIceServers();
      const pc = new RTCPeerConnection({
        iceServers,
        iceTransportPolicy: 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceCandidatePoolSize: 2,
      });
      warmupPeerRef.current = pc;

      // ICE gathering'i tetiklemek için dummy bir data channel aç ve offer oluştur.
      pc.createDataChannel('warmup');
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Daha uzun süreli warm-up: TURN allocation ve candidate gathering
      // tamamlanana kadar peer'i açık tut. Bu, ilk çağrıdaki soğuk başlangıç
      // gecikmesini daha da kısaltır.
      window.setTimeout(() => {
        if (warmupPeerRef.current === pc) {
          pc.close();
          warmupPeerRef.current = null;
        }
      }, 10000);
    } catch {
      // Warm-up başarısız olursa sessizce bırak; gerçek çağrı kendi akışında devam eder.
    }
  }, [getIceServers]);

  const setAudioSessionType = useCallback((type: 'auto' | 'play-and-record' | 'voicechat') => {
    const nav = navigator as unknown as { audioSession?: { type: string; set?: (config: { type: string }) => void } };
    if (!nav.audioSession) return;
    try {
      if (typeof nav.audioSession.set === 'function') {
        nav.audioSession.set({ type });
      } else {
        nav.audioSession.type = type;
      }
    } catch {
      // ignore
    }
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
    } catch {
      // ignore
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  const getRingtoneUrl = useCallback((): string => {
    const base = (import.meta.env.BASE_URL as string) || '/';
    return `${base}ringtone.wav`;
  }, []);

  const getRingbackUrl = useCallback((): string => {
    const base = (import.meta.env.BASE_URL as string) || '/';
    return `${base}ringback.wav`;
  }, []);

  const normalizeSdp = useCallback((sdp: string | undefined | null): string => {
    if (!sdp) return '';
    return (
      sdp
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .trimEnd() + '\r\n'
    );
  }, []);

  const optimizeOpusSdp = useCallback((sdp: string): string => {
    // Normalize line endings to \n for processing; caller will convert back to \r\n.
    let optimized = sdp.replace(/\r\n/g, '\n');

    // Find the OPUS payload type in the audio media section (case-insensitive).
    const opusRtpmapMatch = /a=rtpmap:(\d+) opus\/48000\/2\n/i.exec(optimized);
    if (opusRtpmapMatch) {
      const payloadType = opusRtpmapMatch[1];
      const params = 'useinbandfec=1;usedtx=1;maxplaybackrate=48000;stereo=0;ptime=20;minptime=10;maxptime=60';
      const existingFmtp = new RegExp(`a=fmtp:${payloadType}[^\\n]*\\n`).exec(optimized);
      if (existingFmtp) {
        optimized = optimized.replace(existingFmtp[0], `a=fmtp:${payloadType} ${params}\n`);
      } else {
        optimized = optimized.replace(opusRtpmapMatch[0], `${opusRtpmapMatch[0]}a=fmtp:${payloadType} ${params}\n`);
      }
    }

    // Move OPUS to the front of the audio codec list when possible.
    const audioSectionMatch = /(m=audio \d+ ([A-Z/]+) )([\d ]+)(\n)/i.exec(optimized);
    if (audioSectionMatch) {
      const codecs = audioSectionMatch[3].trim().split(/\s+/);
      const opusPayload = codecs.find((payload) =>
        new RegExp(`a=rtpmap:${payload} opus/48000/2\\n`, 'i').test(optimized)
      );
      if (opusPayload && codecs[0] !== opusPayload) {
        const reordered = [opusPayload, ...codecs.filter((payload) => payload !== opusPayload)].join(' ');
        optimized = optimized.replace(audioSectionMatch[0], `${audioSectionMatch[1]}${reordered}${audioSectionMatch[4]}`);
      }
    }

    return optimized;
  }, []);

  const playRingtone = useCallback(() => {
    initAudioContext();
    if (!ringtoneRef.current) {
      const audio = new Audio(getRingtoneUrl());
      audio.loop = true;
      audio.volume = 1.0;
      audio.preload = 'auto';
      ringtoneRef.current = audio;
    }
    const audio = ringtoneRef.current;
    if (audio.paused) {
      audio.currentTime = 0;
      audio.play().catch(() => {
        // Mobil tarayıcılarda etkileşim gerekebilir; sessizce bırak.
      });
    }
    // AudioContext de varsa resume et; arka planda throttling'e karşı ek tetikleme.
    audioContextRef.current?.resume().catch(() => {});
  }, [getRingtoneUrl, initAudioContext]);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, []);

  const playRingback = useCallback(() => {
    initAudioContext();
    if (!ringbackRef.current) {
      const audio = new Audio(getRingbackUrl());
      audio.loop = true;
      audio.volume = 0.8;
      audio.preload = 'auto';
      ringbackRef.current = audio;
    }
    const audio = ringbackRef.current;
    if (audio.paused) {
      audio.currentTime = 0;
      audio.play().catch(() => {
        // Mobil tarayıcılarda etkileşim gerekebilir; sessizce bırak.
      });
    }
    audioContextRef.current?.resume().catch(() => {});
  }, [getRingbackUrl, initAudioContext]);

  const stopRingback = useCallback(() => {
    if (ringbackRef.current) {
      ringbackRef.current.pause();
      ringbackRef.current.currentTime = 0;
    }
  }, []);

  const vibrate = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate([500, 200, 500]);
    }
  }, []);

  const cleanupPeerConnection = useCallback(() => {
    if (peerConnectionRef.current) {
      const pc = peerConnectionRef.current;
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
      pc.onnegotiationneeded = null;
      pc.close();
      peerConnectionRef.current = null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__pc = undefined;
    }
    targetUserIdRef.current = null;
    iceRestartAttemptsRef.current = 0;
    signalQueueRef.current = [];
    iceCandidateQueueRef.current = [];
    iceCandidateBatchRef.current = [];
    if (iceCandidateBatchTimerRef.current !== null) {
      window.clearTimeout(iceCandidateBatchTimerRef.current);
      iceCandidateBatchTimerRef.current = null;
    }
    if (warmupPeerRef.current) {
      warmupPeerRef.current.close();
      warmupPeerRef.current = null;
    }
    makingOfferRef.current = false;
    pendingReadyForOfferRef.current = null;
    readyForOfferProcessedRef.current = false;
    setConnectionState(null);
  }, []);

  const cleanupLocalMedia = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    setLocalStream(null);
    remoteStreamRef.current = null;
    setRemoteStream(null);
    // Çağrı sonrası iOS audio session'u normal moduna geri döndür;
    // böylece sonraki sesli mesaj/oynatma işlemleri yüksek kalitede çalışır.
    setAudioSessionType('auto');
  }, [setAudioSessionType]);

  const stopDurationTimer = useCallback(() => {
    if (callDurationIntervalRef.current) {
      clearInterval(callDurationIntervalRef.current);
      callDurationIntervalRef.current = null;
    }
  }, []);

  const startDurationTimer = useCallback(() => {
    if (callDurationIntervalRef.current) return;
    const startTime = Date.now();
    callDurationIntervalRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
  }, []);

  const resetCallState = useCallback(() => {
    cleanupPeerConnection();
    cleanupLocalMedia();
    stopDurationTimer();
    releaseWakeLock();
    stopRingtone();
    stopRingback();
    if (visibilityRingtoneIntervalRef.current) {
      clearInterval(visibilityRingtoneIntervalRef.current);
      visibilityRingtoneIntervalRef.current = null;
    }
    setAudioSessionType('auto');
    setActiveCall(null);
    setCallStatus('idle');
    setConnectionState(null);
    setIsMuted(false);
    setIsVideoEnabled(true);
    setIsSpeakerOn(false);
    isSpeakerOnRef.current = false;
    setIsLocalVideoLarge(false);
    setIsBackCamera(false);
    setAudioOutputDevices([]);
    setCallDuration(0);
    setCallError(null);
    prewarmPromiseRef.current = null;
    resetCallStateStore();
  }, [cleanupPeerConnection, cleanupLocalMedia, stopDurationTimer, releaseWakeLock, stopRingtone, stopRingback, setAudioSessionType, resetCallStateStore]);

  const refreshAudioOutputDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outputs = devices.filter((d) => d.kind === 'audiooutput');
      setAudioOutputDevices(outputs);
    } catch {
      // ignore
    }
  }, []);

  const applySpeakerState = useCallback(
    async (speakerOn: boolean) => {
      // Safari / iOS audioSession: 'auto' generally routes to speakerphone,
      // 'play-and-record' generally routes to earpiece/headset.
      // iOS'ta WebRTC bazen kendi session'ını speakerphone olarak ayarlar;
      // kulaklık/avize moduna geçmek için önce reset yapıp sonra tekrar
      // play-and-record'a çekmek daha güvenilir.
      if (speakerOn) {
        setAudioSessionType('auto');
      } else {
        // Ahize (earpiece) moduna geçmek için auto'dan voicechat'e çek.
        // iOS'ta voicechat, play-and-record'dan daha güçlü bir earpiece route
        // garantisi verir; play-and-record bazen speakerphone'a düşebilir.
        setAudioSessionType('auto');
        await new Promise((resolve) => setTimeout(resolve, 30));
        setAudioSessionType('voicechat');
      }

      const audio = remoteAudioElementRef.current;
      if (!audio || !('setSinkId' in audio)) return;

      try {
        const el = audio as HTMLAudioElement & { setSinkId(id: string): Promise<void> };
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outputs = devices.filter((d) => d.kind === 'audiooutput');

        const findDevice = (patterns: RegExp[]) =>
          outputs.find((d) => patterns.some((p) => p.test(d.label)))?.deviceId;

        let deviceId: string | undefined;
        if (speakerOn) {
          // Prefer explicit speaker/loudspeaker, then fall back to default.
          deviceId = findDevice([/speakerphone/i, /loudspeaker/i, /speaker/i, /default/i]);
        } else {
          // Prefer earpiece/receiver/headset/headphone/earbuds, then fall back to default.
          deviceId = findDevice([/earpiece/i, /receiver/i, /headset/i, /headphone/i, /earbuds/i, /default/i]);
        }

        await el.setSinkId(deviceId ?? 'default');
      } catch {
        // ignore
      }
    },
    [setAudioSessionType]
  );

  // Remote stream geldiğinde mevcut hoparlör tercihini uygula.
  useEffect(() => {
    if (remoteStream) {
      applySpeakerState(isSpeakerOnRef.current);
    }
  }, [remoteStream, applySpeakerState]);

  const setRemoteAudioElement = useCallback(
    (element: HTMLAudioElement | null) => {
      remoteAudioElementRef.current = element;
      if (element && remoteStreamRef.current) {
        element.srcObject = remoteStreamRef.current;
        element.play().catch(() => {});
        applySpeakerState(isSpeakerOnRef.current);
      }
    },
    [applySpeakerState]
  );

  const getUserMedia = useCallback(async (callType: 'audio' | 'video' = 'audio', facingMode: 'user' | 'environment' = 'user') => {
    // iOS Safari workaround: getUserMedia öncesinde audioSession'u ahize
    // (voicechat) moduna çekmek, izin sonrası hoparlörden başlamayı önler.
    // Aksi halde cihaz speakerphone'dan başlayabilir.
    // Kaynak: https://stackoverflow.com/q/79401143
    setAudioSessionType('voicechat');
    await initAudioContext();
    try {
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        } as MediaTrackConstraints,
        video:
          callType === 'video'
            ? {
                facingMode: { ideal: facingMode },
                // İlk bağlantı kurulumunu hızlandırmak için düşük çözünürlükte başla;
                // bağlantı kurulduktan sonra kaliteyi yükseltmek mümkün.
                width: { ideal: 640, max: 1280 },
                height: { ideal: 480, max: 720 },
                frameRate: { ideal: 24, max: 30 },
              }
            : false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // Media izni sonrası varsayılan olarak ahize/kulaklık (voicechat) modunu koru;
      // tarayıcı 'auto' yaparsa hoparlöre atlayabilir.
      setAudioSessionType('voicechat');
      localStreamRef.current = stream;
      setLocalStream(stream);
      refreshAudioOutputDevices();
      // Varsayılan çıkışı ahize/kulaklık moduna sabitle; kullanıcı isterse
      // hoparlör butonuyla hoparlöre geçebilir. İlk 2 saniye içinde tekrarlı
      // olarak varsayılan modu güçlendiriyoruz.
      await applySpeakerState(false);
      [0, 100, 250, 500, 1000, 2000].forEach((delay) => {
        setTimeout(() => {
          applySpeakerState(isSpeakerOnRef.current);
        }, delay);
      });
      return stream;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Bilinmeyen hata';
      const mediaLabel = callType === 'video' ? 'Kamera ve mikrofon erişimi gerekli' : 'Mikrofon erişimi gerekli';
      setCallError(`${mediaLabel}: ${message}`);
      throw err;
    }
  }, [setAudioSessionType, initAudioContext, refreshAudioOutputDevices, applySpeakerState]);

  const sendSignal = useCallback(
    async (callId: number, toUserId: number, type: CallSignal['type'], payload: unknown) => {
      const conversationId = activeCallRef.current?.conversation_id;
      let whisperSent = false;

      // Önce WebSocket whisper (client-event) ile doğrudan karşı tarafa gönder.
      // Bu, REST API round-trip'ini kaldırarak signaling gecikmesini azaltır.
      // Kanal hazır değilse veya whisper başarısız olursa REST fallback devreye girer.
      if (conversationId && typeof conversationId === 'number') {
        const sub = subscriptionsRef.current.get(conversationId);
        const channel = sub?.channel as unknown as
          | { whisper?: (event: string, data: unknown) => void }
          | undefined;
        if (channel && typeof channel.whisper === 'function') {
          try {
            channel.whisper('call.signal', {
              call_id: callId,
              from_user_id: user?.id,
              to_user_id: toUserId,
              type,
              payload,
            });
            whisperSent = true;
          } catch (err) {
            console.warn('[WebRTC] Whisper signal failed, falling back to REST:', err);
          }
        }
      }

      // Whisper gönderilemediyse veya kanal hazır değilse REST API fallback.
      if (!whisperSent) {
        try {
          await api.post(`/api/v1/calls/${callId}/signal`, {
            to_user_id: toUserId,
            type,
            payload,
          });
        } catch (err) {
          console.error('Signal send error:', err);
        }
      }
    },
    [user?.id]
  );

  const processIceQueue = useCallback(async () => {
    const pc = peerConnectionRef.current;
    if (!pc || !pc.remoteDescription) return;

    while (iceCandidateQueueRef.current.length > 0) {
      const candidate = iceCandidateQueueRef.current.shift();
      if (!candidate) continue;
      // Geçersiz candidate (sdpMid ve sdpMLineIndex ikisi de null/undefined) atlanır.
      if (
        (candidate.sdpMid === null || candidate.sdpMid === undefined) &&
        (candidate.sdpMLineIndex === null || candidate.sdpMLineIndex === undefined)
      ) {
        continue;
      }
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.warn('ICE candidate add error:', err);
      }
    }
  }, []);

  const processSignalQueue = useCallback(async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;

    while (signalQueueRef.current.length > 0) {
      const signal = signalQueueRef.current.shift();
      if (!signal) continue;

      try {
        if (signal.type === 'offer') {
          if (pc.signalingState !== 'stable') {
            signalQueueRef.current.unshift(signal);
            break;
          }
          await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));
          await processIceQueue();
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          if (activeCallRef.current && targetUserIdRef.current) {
            await sendSignal(activeCallRef.current.id, targetUserIdRef.current, 'answer', answer);
          }
        } else if (signal.type === 'answer') {
          const signalingState = pc.signalingState;
          if (signalingState !== 'have-local-offer') {
            // Stale/duplicate answer; eğer stable ise at, aksi halde bekle.
            if (signalingState === 'stable') {
              continue;
            }
            signalQueueRef.current.unshift(signal);
            break;
          }
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));
          } catch (err) {
            if (pc.signalingState === 'stable' && err instanceof Error && err.name === 'InvalidStateError') {
              continue;
            }
            throw err;
          }
          await processIceQueue();
        }
      } catch (err) {
        console.error('Signal queue processing error:', err);
      }
    }

  }, [sendSignal, processIceQueue]);

  const sendImmediateOffer = useCallback(
    async (toUserId: number) => {
      const pc = peerConnectionRef.current;
      if (!pc || !activeCallRef.current || activeCallRef.current.caller_id !== user?.id) return;
      if (pc.signalingState !== 'stable' || makingOfferRef.current) return;

      const stream = localStreamRef.current;
      if (!stream) {
        // Media izni hâlâ bekleniyor; tekrar denemek için kaydet.
        pendingReadyForOfferRef.current = toUserId;
        readyForOfferProcessedRef.current = false;
        return;
      }

      readyForOfferProcessedRef.current = true;
      pendingReadyForOfferRef.current = null;
      makingOfferRef.current = true;
      try {
        // Arayan prewarm akışında track'ler zaten peer'e eklenmiş olabilir;
        // aynı track'i iki kez eklemek InvalidAccessError ile offer oluşturmayı engeller.
        const existingTrackIds = new Set(
          pc
            .getSenders()
            .map((s) => s.track?.id)
            .filter((id): id is string => typeof id === 'string')
        );
        stream.getTracks().forEach((track) => {
          if (!existingTrackIds.has(track.id)) {
            pc.addTrack(track, stream);
          }
        });
        recordMetric('offerCreatedAt');
        const offer = await pc.createOffer();
        // setLocalDescription'ta orijinal SDP kullan; tarayıcı modify edilmiş SDP'yi
        // reddedebilir. Optimize edilmiş versiyonu sadece signal payload'ında gönder.
        await pc.setLocalDescription(offer);
        const optimizedOffer: RTCSessionDescriptionInit = {
          type: offer.type,
          sdp: normalizeSdp(optimizeOpusSdp(offer.sdp || '')),
        };
        recordMetric('offerSentAt');
        await sendSignal(activeCallRef.current.id, toUserId, 'offer', optimizedOffer);
      } catch (err) {
        console.error('[WebRTC] immediate offer error:', err);
      } finally {
        makingOfferRef.current = false;
      }
    },
    [user, sendSignal, optimizeOpusSdp, normalizeSdp, recordMetric]
  );

  const createPeerConnection = useCallback(
    async (targetUserId: number) => {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }

      const iceServers = await getIceServers();
      const pc = new RTCPeerConnection({
        iceServers,
        iceTransportPolicy: 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceCandidatePoolSize: 10,
      });

      targetUserIdRef.current = targetUserId;

      const flushIceCandidateBatch = () => {
        if (iceCandidateBatchRef.current.length === 0) return;
        const batch = [...iceCandidateBatchRef.current];
        iceCandidateBatchRef.current = [];
        const call = activeCallRef.current;
        if (call) {
          sendSignal(call.id, targetUserId, 'candidate', batch);
        } else {
          // Prewarm aşamasında activeCall henüz set edilmemiş olabilir;
          // candidate'leri geçici olarak sakla, çağrı aktif olduğunda gönder.
          iceCandidateQueueRef.current.push(...batch);
        }
      };

      pc.onicecandidate = (event) => {
        if (!event.candidate) return;
        iceCandidateBatchRef.current.push(event.candidate.toJSON());
        if (iceCandidateBatchTimerRef.current === null) {
          iceCandidateBatchTimerRef.current = window.setTimeout(() => {
            iceCandidateBatchTimerRef.current = null;
            flushIceCandidateBatch();
          }, 50);
        }
      };

      pc.ontrack = (event) => {
        if (event.streams?.[0]) {
          remoteStreamRef.current = event.streams[0];
          setRemoteStream(event.streams[0]);
          // Sesin çalmasını garantilemek için element üzerinde play() tetikle
          remoteAudioElementRef.current?.play().catch(() => {});
          // iOS Safari'de remote stream aktif olduktan sonra tarayıcı
          // otomatik speakerphone'a geçebilir; istenen kulaklık/avize
          // moduna geri zorla.
          applySpeakerState(isSpeakerOnRef.current);
        }
      };

      pc.onconnectionstatechange = () => {
        setConnectionState(pc.connectionState);
        if (pc.connectionState === 'connected') {
          recordMetric('connectedAt');
          setCallStatus('ongoing');
          startDurationTimer();
          iceRestartAttemptsRef.current = 0;
        } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          if (pc.connectionState === 'failed' && iceRestartAttemptsRef.current < 3) {
            pc.restartIce();
            iceRestartAttemptsRef.current += 1;
          } else if (pc.connectionState === 'failed') {
            setCallError('Bağlantı kurulamadı.');
            if (activeCallRef.current) {
              endCallRef.current(activeCallRef.current.id);
            }
          }
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          // ICE tamamlandığında media yolu hazırdır; connectionState connected
          // olmadan önce UI'yi "Bağlandı" durumuna getirerek kullanıcıya yansıyan
          // bekleme süresini kısalt.
          if (callMetricsRef.current.connectedAt === undefined) {
            recordMetric('connectedAt');
            setConnectionState('connected');
            setCallStatus('ongoing');
            startDurationTimer();
            iceRestartAttemptsRef.current = 0;
          }
        } else if (pc.iceConnectionState === 'failed') {
          if (iceRestartAttemptsRef.current < 3) {
            pc.restartIce();
            iceRestartAttemptsRef.current += 1;
          }
        }
      };

      // onnegotiationneeded yerine explicit ready-for-offer flow kullanıyoruz.
      // Böylece arayan ve aranan arasındaki race condition önlenir.
      pc.onnegotiationneeded = null;

      peerConnectionRef.current = pc;
      // Test ortamında RTCPeerConnection istatistiklerine erişim için
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__pc = pc;

      // Peer kurulumu tamamlandıktan sonra bekleyen ready-for-offer varsa tek
      // noktadan (sendImmediateOffer) işleyelim; böylece hem createPeerConnection
      // hem handleSignal aynı offer'ı oluşturmaya çalışmaz.
      const pendingReadyFrom = pendingReadyForOfferRef.current;
      if (pendingReadyFrom !== null && activeCallRef.current?.caller_id === user?.id && pc.signalingState === 'stable') {
        // pendingReadyForOfferRef.current'ı burada temizlemiyoruz;
        // sendImmediateOffer başarılı olduğunda veya stream hazır olunca
        // handleCallAccepted içinde temizlenecek.
        sendImmediateOffer(pendingReadyFrom);
      }

      return pc;
    },
    [getIceServers, sendSignal, startDurationTimer, user, optimizeOpusSdp, normalizeSdp, sendImmediateOffer, applySpeakerState, recordMetric]
  );

  const handleSignal = useCallback(
    async (event: CallSignalEvent) => {
      if (event.to_user_id !== user?.id) return;

      const signal: CallSignal = {
        call_id: event.call_id,
        from_user_id: event.from_user_id,
        to_user_id: event.to_user_id,
        type: event.type,
        payload: event.payload,
      };

      const pc = peerConnectionRef.current;

      try {
        if (signal.type === 'ready-for-offer') {
          // Yeni akışta arayan çağrı kabul edilir edilmez hemen offer gönderir.
          // Bu event, peer/track henüz hazır değilse veya eski arayan
          // client'larından gelen sinyaller için fallback olarak işlenir.
          if (!activeCallRef.current || activeCallRef.current.caller_id !== user?.id) return;

          if (readyForOfferProcessedRef.current) return;

          if (!pc) {
            // Peer henüz hazır değilse caller tarafında; kimden geldiğini kaydet.
            pendingReadyForOfferRef.current = event.from_user_id;
            return;
          }

          if (pc.signalingState !== 'stable' || makingOfferRef.current) return;

          readyForOfferProcessedRef.current = true;
          pendingReadyForOfferRef.current = null;
          await sendImmediateOffer(event.from_user_id);
          return;
        }

        if (!pc) {
          // Peer henüz hazır değilse; offer/answer/candidate gibi işlenmesi gereken
          // sinyalleri uygun kuyruklara at.
          if (signal.type === 'offer' || signal.type === 'answer') {
            signalQueueRef.current.push({ type: signal.type, payload: signal.payload as RTCSessionDescriptionInit });
          } else if (signal.type === 'candidate') {
            iceCandidateQueueRef.current.push(signal.payload as RTCIceCandidateInit);
          }
          return;
        }

        if (signal.type === 'offer') {
          const payload = signal.payload as RTCSessionDescriptionInit;
          recordMetric('offerReceivedAt');
          if (pc.signalingState !== 'stable') {
            signalQueueRef.current.push({ type: 'offer', payload: signal.payload as RTCSessionDescriptionInit });
            return;
          }
          const normalizedOffer: RTCSessionDescriptionInit = {
            type: payload.type,
            sdp: normalizeSdp(payload.sdp),
          };
          await pc.setRemoteDescription(new RTCSessionDescription(normalizedOffer));
          await processIceQueue();
          recordMetric('answerCreatedAt');
          const answer = await pc.createAnswer();
          // setLocalDescription'ta orijinal SDP kullan.
          await pc.setLocalDescription(answer);
          const optimizedAnswer: RTCSessionDescriptionInit = {
            type: answer.type,
            sdp: normalizeSdp(optimizeOpusSdp(answer.sdp || '')),
          };
          if (activeCallRef.current && targetUserIdRef.current) {
            recordMetric('answerSentAt');
            await sendSignal(activeCallRef.current.id, targetUserIdRef.current, 'answer', optimizedAnswer);
          }
          processSignalQueue();
        } else if (signal.type === 'answer') {
          const payload = signal.payload as RTCSessionDescriptionInit;
          recordMetric('answerReceivedAt');
          const answerState = pc.signalingState;
          if (answerState !== 'have-local-offer') {
            // Eğer zaten stable ise muhtemelen duplicate/late answer; bekleme kuyruğuna atma.
            if (answerState === 'stable') {
              return;
            }
            signalQueueRef.current.push({ type: 'answer', payload: signal.payload as RTCSessionDescriptionInit });
            return;
          }
          const normalizedAnswer: RTCSessionDescriptionInit = {
            type: payload.type,
            sdp: normalizeSdp(payload.sdp),
          };
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(normalizedAnswer));
          } catch (err) {
            // State değişmiş olabilir (race); stable ise zaten bağlantı kurulmuştur.
            if (pc.signalingState === 'stable' && err instanceof Error && err.name === 'InvalidStateError') {
              return;
            }
            throw err;
          }
          await processIceQueue();
          processSignalQueue();
        } else if (signal.type === 'candidate') {
          const candidatePayloads = Array.isArray(signal.payload)
            ? (signal.payload as RTCIceCandidateInit[])
            : [signal.payload as RTCIceCandidateInit];
          for (const candidate of candidatePayloads) {
            if (
              (candidate.sdpMid === null || candidate.sdpMid === undefined) &&
              (candidate.sdpMLineIndex === null || candidate.sdpMLineIndex === undefined)
            ) {
              continue;
            }
            if (pc.remoteDescription) {
              try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              } catch (err) {
                console.warn('[WebRTC] ICE candidate add error:', err);
              }
            } else {
              iceCandidateQueueRef.current.push(candidate);
            }
          }
        }
      } catch (err) {
        const payloadHasSdp =
          typeof signal?.payload === 'object' &&
          signal.payload !== null &&
          'sdp' in signal.payload &&
          typeof (signal.payload as { sdp?: unknown }).sdp === 'string';
        console.error('Signal handling error:', err, {
          type: signal?.type,
          payloadKeys:
            signal?.payload && typeof signal.payload === 'object'
              ? Object.keys(signal.payload)
              : null,
          sdpLength: payloadHasSdp ? ((signal.payload as { sdp: string }).sdp.length) : 0,
          sdpPreview: payloadHasSdp ? ((signal.payload as { sdp: string }).sdp.slice(0, 300)) : null,
        });
      }
    },
    [user, sendSignal, processIceQueue, processSignalQueue, optimizeOpusSdp, normalizeSdp, sendImmediateOffer, recordMetric]
  );

  const handleCallInitiated = useCallback(
    (event: CallInitiatedEvent) => {
      if (event.caller.id === user?.id) return;
      if (activeCallRef.current) return;

      recordMetric('callInitiatedBroadcastReceivedAt');

      const caller: User = {
        id: event.caller.id,
        name: event.caller.name,
        surname: event.caller.surname,
        email: '',
        role: 'kullanici',
        role_id: null,
        avatar: event.caller.avatar,
        is_active: true,
        storage_used: 0,
        storage_limit: 0,
        extra_permissions: null,
      };

      const call: ConversationCall = {
        id: event.call_id,
        conversation_id: event.conversation_id,
        caller_id: event.caller.id,
        caller,
        call_type: event.call_type,
        status: 'ringing',
        started_at: event.started_at,
        answered_at: null,
        ended_at: null,
        ended_by: null,
        end_reason: null,
        created_at: event.started_at,
        updated_at: event.started_at,
      };

      setActiveCall(call);
      setCallStatus('ringing');
      playRingtone();
      vibrate();

      // Gelen arama ekranı gösterilirken hemen media+peer hazırlığı başlat;
      // kullanıcı kabul ettiğinde bağlantı kurma süresi kısalır.
      prewarmIncomingCall(event.caller.id, event.call_type);

      // Mümkünse pencereyi öne getir (tarayıcı izin verirse).
      try {
        window.focus();
      } catch {
        // ignore
      }

      // Browser açıkken de native bildirim göster (arka plandaysa service worker push handler zaten çalışır)
      showLocalNotification(`📞 ${caller.name} ${caller.surname}`, {
        body: call.call_type === 'video' ? 'Gelen görüntülü arama' : 'Gelen sesli arama',
        icon: caller.avatar || '/icon-192.png',
        tag: `incoming-call-${call.id}`,
        requireInteraction: true,
        // @ts-expect-error NotificationOptions tipi renotify özelliğini henüz tanımlamıyor; tarayıcılar destekliyor.
        renotify: true,
        vibrate: [500, 200, 500],
        data: {
          type: 'incoming_call',
          call_id: call.id,
          conversation_id: call.conversation_id,
          caller_name: `${caller.name} ${caller.surname}`,
          call_type: call.call_type,
        },
      });

      // Sayfa arka plandaysa ringtone'u düzenli olarak yeniden tetikle.
      // 500 ms aralikla deneyerek background throttling'e karşı dirençli ol.
      if (document.hidden && !visibilityRingtoneIntervalRef.current) {
        visibilityRingtoneIntervalRef.current = setInterval(() => {
          if (activeCallRef.current?.status === 'ringing' && document.hidden) {
            playRingtone();
            vibrate();
          }
        }, 500);
      }
    },
[user?.id, playRingtone, vibrate, recordMetric]
  );

  const handleCallAccepted = useCallback(
    async (event: CallAcceptedEvent) => {
      if (!activeCallRef.current || activeCallRef.current.id !== event.call_id) return;

      recordMetric('callAcceptedBroadcastReceivedAt');

      const isCallee = event.accepted_by.id === user?.id;

      if (isCallee) {
        // Callee zaten acceptCall içinde peer kurulumunu yapti; sadece UI senkronizasyonu.
        setActiveCall((prev) => (prev ? { ...prev, status: 'ongoing' } : prev));
        setCallStatus('connecting');
        stopRingtone();
        stopRingback();
        return;
      }

      // Arayan taraf: çağrı kabul edilir edilmez hemen offer gönder.
      // Yeni akışta ready-for-offer el sıkışmasına gerek yok; aranan taraf
      // offer alınca answer döner. Eski istemcilerden gelebilecek
      // ready-for-offer sinyali handleSignal içinde fallback olarak işlenir.
      if (activeCallRef.current.caller_id !== user?.id) return;

      // Çok katılımcılı aramalarda ilk kabul edenle bağlantı kurulur; sonraki kabul edenler
      // göz ardı edilir (mevcut frontend tek peer connection üzerine kuruludur).
      // Aynı kullanıcıdan tekrar kabul gelirse (sayfa yenileme/reconnect) eski
      // bağlantı kapatılıp yeniden kurulur.
      // Arayan taraf çağrı başlatılırken zaten peer kurduysa (initiateCall prewarm)
      // ve kabul eden kullanıcı beklenen hedefse, mevcut peer'ı koru ve hemen offer gönder.
      if (peerConnectionRef.current && targetUserIdRef.current === event.accepted_by.id) {
        try {
          setActiveCall((prev) => {
            if (!prev) return prev;
            const participants = prev.participants?.map((p) =>
              p.user_id === event.accepted_by.id ? { ...p, status: 'joined' as const } : p
            );
            return { ...prev, status: 'ongoing', participants };
          });
          setCallStatus('connecting');
          stopRingtone();
          stopRingback();
          // ready-for-offer el sıkışması (whisper) broadcast'ten hızlı ulaşıp
          // offer'ı zaten gönderdiyse tekrar gönderme; aksi halde bağlantı
          // kurulmuşken gereksiz renegotiation (çift offer) oluşur.
          if (!readyForOfferProcessedRef.current) {
            await sendImmediateOffer(event.accepted_by.id);
          }
        } catch (err) {
          console.error('Call accepted handling error (prewarm path):', err);
          setCallError('Çağrı bağlantısı kurulamadı.');
          if (activeCallRef.current) {
            endCallRef.current(activeCallRef.current.id);
          }
        }
        return;
      }

      // Prewarm yoksa peer'ı yeniden kur.
      if (peerConnectionRef.current) {
        if (targetUserIdRef.current !== event.accepted_by.id) {
          return;
        }
        cleanupPeerConnection();
      }

      try {
        setActiveCall((prev) => {
          if (!prev) return prev;
          const participants = prev.participants?.map((p) =>
            p.user_id === event.accepted_by.id ? { ...p, status: 'joined' as const } : p
          );
          return { ...prev, status: 'ongoing', participants };
        });
        setCallStatus('connecting');
        stopRingtone();
        stopRingback();

        const callType = activeCallRef.current.call_type ?? 'audio';

        // Peer connection'ı hemen kur; media izni paralel olarak alınsın.
        await createPeerConnection(event.accepted_by.id);

        const stream = localStreamRef.current ? localStreamRef.current : await getUserMedia(callType);
        localStreamRef.current = stream;
        setLocalStream(stream);
        setIsVideoEnabled(callType === 'video');

        // Hemen offer oluştur ve gönder; ready-for-offer el sıkışmasını bekleme.
        // Karşı taraf henüz hazır değilse offer signalQueue'da bekleyecektir.
        // ready-for-offer zaten offer gönderdiyse tekrar gönderme (çift offer önlemi).
        if (!readyForOfferProcessedRef.current) {
          await sendImmediateOffer(event.accepted_by.id);
        }
      } catch (err) {
        console.error('Call accepted handling error:', err);
        setCallError('Çağrı bağlantısı kurulamadı.');
        if (activeCallRef.current) {
          endCallRef.current(activeCallRef.current.id);
        }
      }
    },
    [
      user?.id,
      getUserMedia,
      createPeerConnection,
      cleanupPeerConnection,
      processSignalQueue,
      sendImmediateOffer,
      stopRingtone,
      stopRingback,
      recordMetric,
    ]
  );

  const handleCallRejected = useCallback(
    (event: CallRejectedEvent) => {
      if (!activeCallRef.current || activeCallRef.current.id !== event.call_id) return;
      if (event.rejected_by.id === user?.id) return;

      // Katılımcı listesi henüz yüklenmediyse (örn. broadcast event ile açılan gelen arama)
      // güvenli taraf tut: aramayı sonlandırma, sadece UI'ı koru.
      const currentParticipants = activeCallRef.current.participants;
      if (!currentParticipants) {
        return;
      }

      // Ret eden katılımcının durumunu güncelle.
      const participants = currentParticipants.map((p) =>
        p.user_id === event.rejected_by.id ? { ...p, status: 'rejected' as const } : p
      );

      // Hâlâ kabul bekleyen veya bağlı başka katılımcı varsa aramayı sonlandırma.
      const hasRemainingParticipants = participants.some(
        (p) => (p.status === 'invited' || p.status === 'joined') && p.user_id !== user?.id
      );
      const pcState = peerConnectionRef.current?.connectionState;
      const isPeerConnected = pcState === 'connected' || pcState === 'connecting';
      if (hasRemainingParticipants || isPeerConnected) {
        setActiveCall((prev) => (prev ? { ...prev, participants } : prev));
        return;
      }

      resetCallState();
    },
    [user?.id, resetCallState]
  );

  const handleCallEnded = useCallback(
    (event: CallEndedEvent) => {
      if (!activeCallRef.current || activeCallRef.current.id !== event.call_id) return;
      resetCallState();
    },
    [resetCallState]
  );

  // Abonelik handler'larını her render'da güncel callback'lere bağla.
  handleCallInitiatedRef.current = handleCallInitiated;
  handleCallAcceptedRef.current = handleCallAccepted;
  handleCallRejectedRef.current = handleCallRejected;
  handleCallEndedRef.current = handleCallEnded;
  handleSignalRef.current = handleSignal;

  const subscribeToUserChannel = useCallback(async () => {
    if (!user?.id) return;

    const echo = getEcho();
    if (!echo) return;

    // Önceki aboneliği temizle (StrictMode veya hızlı re-render senaryolarında
    // eski channel objesi üzerindeki listener'lar boşa düşmesin).
    userChannelRef.current?.cleanup();
    userChannelRef.current = null;

    const channel = echo.private(`App.Models.User.${user.id}`);
    channel.listen('.call.initiated', (e: CallInitiatedEvent) => handleCallInitiatedRef.current(e));

    userChannelRef.current = {
      channel,
      cleanup: () => {
        try {
          channel.stopListening('.call.initiated');
        } catch {
          // ignore
        }
      },
    };
  }, [user?.id]);

  // Geliştirme/test ortamında gelen arama akışını doğrudan tetiklemek için
  // test senaryolarına yardımcı bir pencere fonksiyonu sunar.
  useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__triggerIncomingCall = handleCallInitiated;
    }
    return () => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (window as any).__triggerIncomingCall;
      }
    };
  }, [handleCallInitiated]);

  const subscribeToConversation = useCallback(
    async (conversationId: number) => {
      if (subscriptionsRef.current.has(conversationId)) return;

      const echo = getEcho();
      if (!echo) return;

      const channelName = `conversation.${conversationId}`;
      const channel = echo.private(channelName);

      // Aynı channel üzerine tekrar listener eklenmesini önle (StrictMode / race durumları).
      const existingSub = subscriptionsRef.current.get(conversationId);
      if (existingSub?.channel === channel) {
        return;
      }

      const callEvents = [
        { event: '.call.initiated', handler: (e: CallInitiatedEvent) => handleCallInitiatedRef.current(e) },
        { event: '.call.accepted', handler: (e: CallAcceptedEvent) => handleCallAcceptedRef.current(e) },
        { event: '.call.rejected', handler: (e: CallRejectedEvent) => handleCallRejectedRef.current(e) },
        { event: '.call.ended', handler: (e: CallEndedEvent) => handleCallEndedRef.current(e) },
        { event: '.call.signal', handler: (e: CallSignalEvent) => handleSignalRef.current(e) },
      ];

      callEvents.forEach(({ event, handler }) => {
        channel.stopListening(event);
        channel.listen(event, handler);
      });

      // WebSocket client-event (whisper) ile gelen sinyalleri dinle.
      // Bu, REST API'sine gitmeden doğrudan peer-to-peer sinyal iletimini sağlar.
      const handleClientSignal = (e: {
        call_id?: number;
        from_user_id?: number;
        to_user_id?: number;
        type?: string;
        payload?: unknown;
      }) => {
        if (typeof e.to_user_id !== 'number' || e.to_user_id !== user?.id || e.from_user_id === user?.id) return;
        if (!e.call_id || !e.type) return;
        handleSignalRef.current({
          call_id: e.call_id,
          from_user_id: e.from_user_id ?? 0,
          to_user_id: e.to_user_id,
          type: e.type as CallSignal['type'],
          payload: e.payload,
        });
      };
      try {
        const whisperChannel = channel as unknown as {
          listenForWhisper?: (event: string, handler: (e: unknown) => void) => void;
          stopListeningForWhisper?: (event: string) => void;
        };
        // Önceki listener'ı kaldır (StrictMode / race durumları).
        // NOT: stopListening('client-call.signal') EventFormatter namespace'i
        // ('App.Events.') eklediği için whisper binding'ini kaldıramaz;
        // stopListeningForWhisper('.client-' prefix'i ile) doğru hedefi unbind eder.
        // Aksi halde her yeniden abonelikte listener çoğalır ve sinyaller çift işlenir.
        if (typeof whisperChannel.stopListeningForWhisper === 'function') {
          whisperChannel.stopListeningForWhisper('call.signal');
        }
        // Laravel Echo / Pusher whisper event'ini dinle.
        if (typeof whisperChannel.listenForWhisper === 'function') {
          whisperChannel.listenForWhisper(
            'call.signal',
            (e: unknown) =>
              handleClientSignal(e as typeof handleClientSignal extends (arg: infer P) => void ? P : never)
          );
        }
      } catch {
        // ignore
      }

      subscriptionsRef.current.set(conversationId, {
        channel,
        cleanup: () => {
          callEvents.forEach(({ event }) => {
            try {
              channel.stopListening(event);
            } catch {
              // ignore
            }
          });
          try {
            const whisperChannel = channel as unknown as {
              stopListeningForWhisper?: (event: string) => void;
            };
            // stopListening namespace eklediği için whisper binding'ini kaldıramaz
            // (yukarıdaki nota bakınız); whisper'a özel API kullanılır.
            whisperChannel.stopListeningForWhisper?.('call.signal');
          } catch {
            // ignore
          }
        },
      });

      try {
        const res = await api.get<{ data: ConversationCall | null }>(
          `/api/v1/conversations/${conversationId}/calls/active`
        );
        const call = res.data.data;
        if (call && !activeCallRef.current) {
          setActiveCall(call);
          if (call.status === 'ringing') {
            setCallStatus(call.caller_id === user?.id ? 'outgoing' : 'ringing');
            if (call.caller_id !== user?.id) {
              playRingtone();
              vibrate();
            } else {
              playRingtone();
            }
          } else if (call.status === 'ongoing') {
            setCallStatus('connecting');
            startDurationTimer();
            try {
              const stream = await getUserMedia(call.call_type);
              localStreamRef.current = stream;
              setLocalStream(stream);
              const targetUserId =
                call.caller_id === user?.id
                  ? call.participants?.find((p) => p.user_id !== user?.id && p.status === 'joined')?.user_id
                  : call.caller_id;
              if (targetUserId) {
                const isCaller = call.caller_id === user?.id;
                const pc = await createPeerConnection(targetUserId);
                // Callee olarak peer kurulumu tamamlaninca track'leri ekle ve ready bildir.
                // Caller olarak track'leri ready-for-offer geldikten sonra ekleyeceğiz.
                if (!isCaller) {
                  stream.getTracks().forEach((track) => pc.addTrack(track, stream));
                }
                requestWakeLock();
                if (!isCaller) {
                  await sendSignal(call.id, call.caller_id, 'ready-for-offer', {});
                }
              }
            } catch {
              // ignore reconnect errors; user can manually end/retry
            }
          }
        }
      } catch (err) {
        console.error('Active call sync error:', err);
      }
    },
    [user, handleCallInitiated, handleCallAccepted, handleCallRejected, handleCallEnded, handleSignal, getUserMedia, createPeerConnection, playRingtone, vibrate, requestWakeLock, startDurationTimer, sendSignal]
  );

  const unsubscribeFromConversation = useCallback((conversationId: number) => {
    const sub = subscriptionsRef.current.get(conversationId);
    if (sub) {
      sub.cleanup();
      subscriptionsRef.current.delete(conversationId);
    }
  }, []);

  const initiateCall = useCallback(
    async (conversationId: number, callType: 'audio' | 'video' = 'audio', earlyTargetUserId?: number) => {
      if (!user?.id) return;
      if (activeCallRef.current) {
        setCallError('Halihazırda bir çağrı devam ediyor.');
        return;
      }

      resetMetrics();
      recordMetric('callInitiatedAt');

      let callApiController: AbortController | null = null;

      try {
        // getUserMedia ve /call API'sini paralel başlat; böylece media izni
        // bekleme süresi ile API yanıt bekleme süresi üst üste binmez.
        callApiController = new AbortController();

        const mediaPromise = getUserMedia(callType).then(async (stream) => {
          setIsVideoEnabled(callType === 'video');
          // Çağrı başlangıcında ses çıkışını varsayılan ahize/kulaklık moduna sabitle.
          setIsSpeakerOn(false);
          isSpeakerOnRef.current = false;
          await applySpeakerState(false);
          return stream;
        });

        // Ringback'i API çağrısından önce başlat; böylece buton tıklama user-gesture
        // context'i henüz taze iken audio.play() tetiklenir.
        playRingback();

        // Arka planda gelen arama bildirimleri için izin iste (user-gesture context'inde).
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
          subscribeToPushNotifications().catch(() => {});
        }

        // Eğer arayici conversation member'larini onceden biliyorsa hedef kullaniciyi
        // API yanitini beklemeden belirle; peer connection kurulumunu hemen baslat.
        let peerPrewarmPromise: Promise<RTCPeerConnection | null> | null = null;
        if (earlyTargetUserId) {
          peerPrewarmPromise = createPeerConnection(earlyTargetUserId).catch(() => null);
        }

        const callApiPromise = api.post<{ data: ConversationCall }>(
          `/api/v1/conversations/${conversationId}/call`,
          { call_type: callType },
          { signal: callApiController.signal }
        );

        // API yanıtı gelir gelmez hedef kullanıcıyı belirleyip peer kurulumunu
        // başlat; media izni hâlâ devam ediyor olabilir. Böylece media hazır
        // olduğunda track ekleme ve offer gönderme daha hızlı gerçekleşir.
        callApiPromise
          .then((res) => {
            const call = res.data.data;
            const targetUserId = call.participants?.find((p) => p.user_id !== user.id)?.user_id;
            if (targetUserId && !peerPrewarmPromise) {
              peerPrewarmPromise = createPeerConnection(targetUserId).catch(() => null);
            }
          })
          .catch(() => {});

        const [stream, res] = await Promise.all([mediaPromise, callApiPromise]);
        const call = res.data.data;
        setActiveCall(call);
        setCallStatus('outgoing');

        // Çağrı kanalına abone olmayı bekle; böylece ret/red gibi event'ler
        // kaçırılmaz. Özellikle yeni oluşturulmuş sohbetlerde abonelik henüz
        // kurulmamış olabilir.
        await subscribeToConversation(conversationId);

        // Arayan tarafı da önceden hazırla; böylece callee kabul ettiğinde
        // hemen offer gönderilir ve "bekleniyor" süresi kısalır.
        const targetUserId = earlyTargetUserId ?? call.participants?.find((p) => p.user_id !== user.id)?.user_id;
        if (targetUserId) {
          try {
            const pc = peerPrewarmPromise
              ? await peerPrewarmPromise
              : await createPeerConnection(targetUserId);
            if (pc && stream) {
              const existingTrackIds = new Set(
                pc
                  .getSenders()
                  .map((s) => s.track?.id)
                  .filter((id): id is string => typeof id === 'string')
              );
              stream.getTracks().forEach((track) => {
                if (!existingTrackIds.has(track.id)) {
                  pc.addTrack(track, stream);
                }
              });
            }
          } catch (err) {
            console.error('[WebRTC] caller prewarm error:', err);
          }
        }

        requestWakeLock();
      } catch (err) {
        callApiController?.abort();
        console.error('Initiate call error:', err);
        const isMediaError =
          err instanceof Error &&
          (err.name === 'NotAllowedError' || err.name === 'NotFoundError' || err.name === 'AbortError');
        const mediaLabel = callType === 'video' ? 'Kamera ve mikrofon erişimi gerekli' : 'Mikrofon erişimi gerekli';
        const message = isMediaError
          ? `${mediaLabel}: ${err.message}`
          : (err as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message ||
            (err as Error).message ||
            'Çağrı başlatılamadı.';
        setCallError(message);
        stopRingback();
        cleanupLocalMedia();
      }
    },
    [user?.id, getUserMedia, requestWakeLock, playRingback, stopRingback, cleanupLocalMedia, createPeerConnection, resetMetrics, recordMetric, subscribeToConversation]
  );

  // Gelen arama modalı açılır açılmaz media izni ve peer connection
  // hazırlığı başlatılır; böylece kullanıcı "Kabul Et"e bastığında
  // ICE aday toplama ve media erişimi çoktan tamamlanmış olur.
  // Media ve peer kurulumunu tamamen paralel başlatmak, ardışık bekleme
  // sürelerini ortadan kaldırır.
  function prewarmIncomingCall(callerId: number, callType: 'audio' | 'video') {
    if (peerConnectionRef.current || localStreamRef.current || prewarmPromiseRef.current) return;
    prewarmPromiseRef.current = (async () => {
      try {
        const [stream, pc] = await Promise.all([
          getUserMedia(callType),
          createPeerConnection(callerId),
        ]);
        localStreamRef.current = stream;
        setLocalStream(stream);
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      } catch (err) {
        console.error('[WebRTC] prewarm error:', err);
      } finally {
        prewarmPromiseRef.current = null;
      }
    })();
  }

  const acceptCall = useCallback(
    async (callId: number) => {
      if (!user?.id) return;

      recordMetric('acceptClickedAt');

      const callType = activeCallRef.current?.call_type ?? 'audio';
      const callerId = activeCallRef.current?.caller_id;
      if (!callerId) {
        setCallError('Aktif çağrı bulunamadı.');
        return;
      }

      // Optimistic: Kabul butonuna basar basmaz arayana hazır olduğumuzu
      // bildir. Böylece arayan, accept API yanıtı ve CallAccepted broadcast'ini
      // beklemeden hemen offer oluşturup göndermeye başlar; signaling süresi
      // ile accept API işlem süresi paralel hale gelir.
      setCallStatus('connecting');
      stopRingtone();
      sendSignal(callId, callerId, 'ready-for-offer', {}).catch(() => {});

      try {
        // Devam eden prewarm varsa bitmesini bekle; bu sayede media+peer
        // hazırlandığında accept daha hızlı tamamlanır.
        if (prewarmPromiseRef.current) {
          await prewarmPromiseRef.current;
        }

        // Eğer prewarm sırasında media+peer hazırlandıysa yeniden oluşturma;
        // aksi halde paralel olarak accept API ve media izni al.
        const hadPrewarm =
          !!peerConnectionRef.current && targetUserIdRef.current === callerId && !!localStreamRef.current;

        const [response, stream] = await Promise.all([
          api.post<{ data: ConversationCall }>(`/api/v1/calls/${callId}/accept`),
          hadPrewarm ? Promise.resolve(localStreamRef.current!) : getUserMedia(callType),
        ]);
        recordMetric('acceptApiRespondedAt');

        const call = response.data.data ?? activeCallRef.current;
        if (!call || call.id !== callId) {
          throw new Error('Aktif çağrı bulunamadı.');
        }

        setActiveCall((prev) =>
          prev ? { ...prev, status: 'ongoing', participants: call.participants } : prev
        );

        if (!localStreamRef.current) {
          localStreamRef.current = stream;
          setLocalStream(stream);
        }

        const existingPc = peerConnectionRef.current;
        const existingTarget = targetUserIdRef.current;
        const pc =
          existingPc && existingTarget === callerId ? existingPc : await createPeerConnection(callerId);

        if (!existingPc || existingTarget !== callerId) {
          stream.getTracks().forEach((track) => pc.addTrack(track, stream));
        }

        // Çağrı başlangıcında ses çıkışını varsayılan ahize/kulaklık moduna sabitle.
        setIsSpeakerOn(false);
        isSpeakerOnRef.current = false;
        await applySpeakerState(false);

        requestWakeLock();
        processSignalQueue();
      } catch (err) {
        console.error('Accept call error:', err);
        const message =
          (err as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message ||
          (err as Error).message ||
          'Çağrı kabul edilemedi.';
        setCallError(message);
        setCallStatus('ringing');
        // Notify caller that we could not accept.
        api.post(`/api/v1/calls/${callId}/reject`).catch(() => {});
      }
    },
    [user?.id, stopRingtone, getUserMedia, createPeerConnection, requestWakeLock, processSignalQueue, recordMetric, sendSignal]
  );

  const rejectCall = useCallback(
    async (callId: number) => {
      try {
        await api.post(`/api/v1/calls/${callId}/reject`);
      } catch (err) {
        console.error('Reject call error:', err);
      } finally {
        resetCallState();
      }
    },
    [resetCallState]
  );

  const endCall = useCallback(
    async (callId: number) => {
      try {
        await api.post(`/api/v1/calls/${callId}/end`);
      } catch (err) {
        console.error('End call error:', err);
      } finally {
        resetCallState();
      }
    },
    [resetCallState]
  );

  endCallRef.current = endCall;

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextMuted = !isMuted;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    setIsMuted(nextMuted);
  }, [isMuted]);

  const toggleVideo = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextEnabled = !isVideoEnabled;
    stream.getVideoTracks().forEach((track) => {
      track.enabled = nextEnabled;
    });
    setIsVideoEnabled(nextEnabled);
  }, [isVideoEnabled]);

  const toggleLocalVideoSize = useCallback(() => {
    setIsLocalVideoLarge((prev) => !prev);
  }, []);

  const getVideoTrack = useCallback(async (facingMode: 'user' | 'environment') => {
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        facingMode: { ideal: facingMode },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30, max: 30 },
      },
    };
    const newStream = await navigator.mediaDevices.getUserMedia(constraints);
    const videoTrack = newStream.getVideoTracks()[0];
    if (!videoTrack) {
      newStream.getTracks().forEach((t) => t.stop());
      throw new Error('Video track alınamadı.');
    }
    return videoTrack;
  }, []);

  const switchCamera = useCallback(async () => {
    const stream = localStreamRef.current;
    const pc = peerConnectionRef.current;
    if (!stream) return;

    const oldVideoTrack = stream.getVideoTracks()[0];
    if (!oldVideoTrack) return;

    const nextFacingMode = isBackCamera ? 'user' : 'environment';
    setIsBackCamera((prev) => !prev);

    try {
      const newVideoTrack = await getVideoTrack(nextFacingMode);

      // Peer connection'daki video sender'ı yeni track ile değiştir.
      if (pc) {
        const videoSender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (videoSender) {
          await videoSender.replaceTrack(newVideoTrack);
        } else {
          pc.addTrack(newVideoTrack, stream);
        }
      }

      // Local stream'deki eski video track'i yeni ile değiştir.
      stream.removeTrack(oldVideoTrack);
      stream.addTrack(newVideoTrack);
      localStreamRef.current = stream;
      setLocalStream(stream);
      setIsVideoEnabled(true);

      // Eski kamerayı serbest bırak.
      oldVideoTrack.stop();
    } catch (err) {
      console.error('Switch camera error:', err);
      setCallError('Kamera değiştirilemedi.');
      setIsBackCamera((prev) => !prev);
    }
  }, [isBackCamera, getVideoTrack]);

  const toggleSpeaker = useCallback(async () => {
    console.log('[CallProvider] toggleSpeaker called, current isSpeakerOn:', isSpeakerOn);
    await initAudioContext();
    const nextSpeaker = !isSpeakerOn;
    console.log('[CallProvider] toggleSpeaker nextSpeaker:', nextSpeaker);
    setIsSpeakerOn(nextSpeaker);
    await applySpeakerState(nextSpeaker);
    await refreshAudioOutputDevices();
    console.log('[CallProvider] toggleSpeaker done');
  }, [isSpeakerOn, applySpeakerState, initAudioContext, refreshAudioOutputDevices]);

  const clearCallError = useCallback(() => setCallError(null), []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activeCallRef.current) {
        navigator.sendBeacon?.(`/api/v1/calls/${activeCallRef.current.id}/end`, new Blob([], { type: 'application/json' }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;
    const bc = new BroadcastChannel('rizmir-call');
    bc.onmessage = (event) => {
      if (event.data?.type === 'call-active' && activeCallRef.current && event.data?.callId !== activeCallRef.current.id) {
        endCallRef.current(activeCallRef.current.id);
      }
    };
    broadcastChannelRef.current = bc;
    return () => {
      bc.close();
      broadcastChannelRef.current = null;
    };
  }, []);

  // Sayfa yenilendiğinde ya da ilk açılışta halihazırda devam eden bir çağrı varsa
  // global aktif çağrı endpoint'inden senkronize et.
  // Ayrıca bildirimden gelen ?call={id}&action=accept|reject query parametrelerini
  // işle ve uygulamayı açtıktan sonra otomatik kabul/reddet.
  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    const syncActiveCall = async () => {
      try {
        const res = await api.get<{ data: ConversationCall | null }>('/api/v1/calls/active');
        const call = res.data.data;

        const params = new URLSearchParams(window.location.search);
        const callIdFromUrl = params.get('call');
        const actionFromUrl = params.get('action');

        // URL'den gelen action parametresini bir kere isle ve temizle.
        if (callIdFromUrl && actionFromUrl && call && String(call.id) === callIdFromUrl && !cancelled) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('call');
          cleanUrl.searchParams.delete('action');
          window.history.replaceState({}, '', cleanUrl.toString());

          setActiveCall(call);
          if (call.status === 'ringing') {
            setCallStatus(call.caller_id === user.id ? 'outgoing' : 'ringing');
          } else if (call.status === 'ongoing') {
            setCallStatus('connecting');
          }

          if (actionFromUrl === 'accept') {
            await acceptCall(call.id);
          } else if (actionFromUrl === 'reject') {
            await rejectCall(call.id);
          }
          return;
        }

        if (!call || cancelled) return;

        // Konuşma kanalı ConversationPage tarafından açılacak; burada sadece
        // aktif çağrı state'ini ve medya izinlerini geri yükleyip modal'ın
        // görünmesini sağlıyoruz.
        setActiveCall(call);
        if (call.status === 'ringing') {
          setCallStatus(call.caller_id === user.id ? 'outgoing' : 'ringing');
          if (call.caller_id !== user.id) {
            playRingtone();
            vibrate();
          } else {
            playRingtone();
          }
        } else if (call.status === 'ongoing') {
          setCallStatus('connecting');
          startDurationTimer();
        }
      } catch (err) {
        console.error('Global active call sync error:', err);
      }
    };

    syncActiveCall();
    return () => {
      cancelled = true;
    };
  }, [user?.id, playRingtone, vibrate, startDurationTimer, acceptCall, rejectCall]);

  // Kullanıcı uygulamaya girdiğinde kullanıcı kanalına ve mevcut
  // sohbetlerine abone ol. Böylece sohbet sayfası dışındaki her sayfada
  // gelen arama modalı görünür.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const subscribeAll = async () => {
      try {
        await subscribeToUserChannel();

        const res = await api.get<{ data: Array<{ id: number }> }>('/api/v1/conversations');
        const conversations = res.data.data || [];
        for (const conv of conversations) {
          if (cancelled) break;
          await subscribeToConversation(conv.id);
          permanentConversationsRef.current.add(conv.id);
        }
      } catch (err) {
        console.error('Conversation subscription sync error:', err);
      }
    };

    subscribeAll();

    // TURN credential'larını ve allocation warm-up'ını ilk çağrı öncesi önceden yap;
    // böylece createPeerConnection beklenmeden ICE sunucu listesine sahip olur ve
    // TURN sunucusuyla ilk el sıkışma gecikmesi kısalır.
    warmupIceServers().catch(() => {});
    return () => {
      cancelled = true;
      // StrictMode veya user değişimi durumlarında eski abonelikleri temizle;
      // aksi halde channel objesi değişse bile listener eski (artık bağlı olmayan)
      // kanalda kalır ve event'ler kaçırılır.
      permanentConversationsRef.current.clear();
      userChannelRef.current?.cleanup();
      userChannelRef.current = null;
      subscriptionsRef.current.forEach((sub) => sub.cleanup());
      subscriptionsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Aktif çağrı oluştuğunda ilgili sohbet kanalına abone ol; böylece
  // kabul/red/signaling event'leri her sayfada çalışır.
  useEffect(() => {
    if (!activeCall?.conversation_id) return;
    const conversationId = activeCall.conversation_id;
    subscribeToConversation(conversationId);
    return () => {
      // Kalıcı (subscribeAll) abonelikleri koru; aksi halde çağrı biterken
      // kanal aboneliği düşer ve sonraki gelen arama bu sohbetten alınamaz.
      if (!permanentConversationsRef.current.has(conversationId)) {
        unsubscribeFromConversation(conversationId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall?.conversation_id]);

  useEffect(() => {
    if (activeCall && broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ type: 'call-active', callId: activeCall.id });
    }
  }, [activeCall]);

  // Stop ringtone/ringback as soon as the peer connection is established
  // (or even when it starts connecting) so users don't hear ringing after
  // they have answered the call.
  useEffect(() => {
    if (connectionState === 'connecting' || connectionState === 'connected') {
      stopRingtone();
      stopRingback();
    }
    if (connectionState === 'connected') {
      startDurationTimer();
      applySpeakerState(isSpeakerOnRef.current);
    }
  }, [connectionState, stopRingtone, stopRingback, startDurationTimer, applySpeakerState]);

  // Sayfa görünürlüğü değiştiğinde ringtone interval'ini yönet
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (visibilityRingtoneIntervalRef.current) {
          clearInterval(visibilityRingtoneIntervalRef.current);
          visibilityRingtoneIntervalRef.current = null;
        }
        return;
      }

      if (activeCallRef.current?.status === 'ringing' && !visibilityRingtoneIntervalRef.current) {
        visibilityRingtoneIntervalRef.current = setInterval(() => {
          if (activeCallRef.current?.status === 'ringing' && document.hidden) {
            playRingtone();
            vibrate();
          }
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityRingtoneIntervalRef.current) {
        clearInterval(visibilityRingtoneIntervalRef.current);
        visibilityRingtoneIntervalRef.current = null;
      }
    };
  }, [playRingtone, vibrate]);

  // Kullanıcı ilk etkileşiminde (click/touch/keydown) AudioContext'i resume et;
  // böylece mobil tarayıcılarda autoplay kısıtlaması aşılır ve zil sesi çalabilir.
  useEffect(() => {
    const unlockAudio = () => {
      initAudioContext();
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
    document.addEventListener('keydown', unlockAudio);

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, [initAudioContext]);

  // Service worker'dan gelen bildirim tıklama/red mesajlarını dinle
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data) return;

      if (data.type === 'incoming_call_notification_click') {
        const call = activeCallRef.current;
        if (!call || call.id !== data.call_id) return;

        if (data.action === 'accept') {
          acceptCall(call.id);
        } else if (data.action === 'reject') {
          rejectCall(call.id);
        } else {
          // Bildirime tıklama: sayfa öne çıkar, modal zaten görünür
          window.focus();
        }
        return;
      }

      if (data.type === 'incoming_call_notification_close') {
        // Kullanıcı gelen arama bildirimini kapatırsa reddet olarak işle
        const call = activeCallRef.current;
        if (call && call.id === data.call_id) {
          rejectCall(call.id);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [acceptCall, rejectCall]);

  useEffect(() => {
    const subs = subscriptionsRef.current;
    return () => {
      subs.forEach((sub) => sub.cleanup());
      subs.clear();
      userChannelRef.current?.cleanup();
      userChannelRef.current = null;
      resetCallState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Context value sadece stream'ler ve stabil aksiyonları taşır; yüksek frekanslı
  // state (callDuration, callStatus, flag'ler vb.) useCallStore üzerinden okunur.
  // Böylece arama sırasında 1 Hz callDuration tick'i tüm layout'u re-render etmez.
  const value = useMemo(
    () => ({
      localStream,
      remoteStream,
      audioOutputDevices,
      subscribeToConversation,
      unsubscribeFromConversation,
      initiateCall,
      acceptCall,
      rejectCall,
      endCall,
      toggleMute,
      toggleVideo,
      toggleSpeaker,
      switchCamera,
      toggleLocalVideoSize,
      setRemoteAudioElement,
      clearCallError,
      resetCallState,
    }),
    [
      localStream,
      remoteStream,
      audioOutputDevices,
      subscribeToConversation,
      unsubscribeFromConversation,
      initiateCall,
      acceptCall,
      rejectCall,
      endCall,
      toggleMute,
      toggleVideo,
      toggleSpeaker,
      switchCamera,
      toggleLocalVideoSize,
      setRemoteAudioElement,
      clearCallError,
      resetCallState,
    ]
  );

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
}
